import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, ChevronDown } from 'lucide-react';
import SubHeader from '../../components/common/SubHeader/SubHeader';
import { getCreditNoteList, getAllPermissionByModuleCodeUserId } from '../../utils/lead';
import { getSession } from '../../getSession';
import { handleLogout } from '../../utils/logout';
import { PermissionDeniedComponent } from '../../hooks/useModulePermissions';
import CreditNoteList from './components/creditnote/list';
import './CreditNoteModule.css';

// ── Hidden module code for this page ──────────────────────────────────────────
const MODULE_CODE = 'PAG10384';

const formatDate = (date) =>
    date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-');

const parseDateStr = (str) => {
    // str like "08-May-2026"
    const [d, m, y] = str.split('-');
    return new Date(`${m} ${d} ${y}`);
};

const toInputValue = (str) => {
    // "08-May-2026" → "2026-05-08"
    try {
        const d = parseDateStr(str);
        return d.toISOString().split('T')[0];
    } catch { return ''; }
};

const fromInputValue = (val) => {
    // "2026-05-08" → "08-May-2026"
    if (!val) return '';
    const d = new Date(val + 'T00:00:00');
    return formatDate(d);
};

const PRESETS = [
    { label: 'Today', getDates: () => { const t = new Date(); return [t, t]; } },
    { label: 'Yesterday', getDates: () => { const t = new Date(); t.setDate(t.getDate() - 1); return [t, t]; } },
    { label: 'Last 7 Days', getDates: () => { const e = new Date(); const s = new Date(); s.setDate(s.getDate() - 6); return [s, e]; } },
    { label: 'Last 30 Days', getDates: () => { const e = new Date(); const s = new Date(); s.setDate(s.getDate() - 29); return [s, e]; } },
    { label: 'This Month', getDates: () => { const n = new Date(); return [new Date(n.getFullYear(), n.getMonth(), 1), new Date(n.getFullYear(), n.getMonth() + 1, 0)]; } },
    { label: 'Last Month', getDates: () => { const n = new Date(); return [new Date(n.getFullYear(), n.getMonth() - 1, 1), new Date(n.getFullYear(), n.getMonth(), 0)]; } },
    { label: 'Custom Range', getDates: null },
];

const CreditNoteModule = ({ onAddClick, onSettingsClick, onShowClick, onEditClick, onCancelClick, onDeleteClick, onRequestApprovalClick }) => {
    const navigate = useNavigate();
    const { userId, TokenId } = getSession();
    const [viewMode, setViewMode] = useState('table');
    const [searchQuery, setSearchQuery] = useState('');
    const [dateRange, setDateRange] = useState({
        from: formatDate(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)),
        to: formatDate(new Date())
    });
    const [statusFilter, setStatusFilter] = useState('');
    const [creditNotes, setCreditNotes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [pagination, setPagination] = useState({ start: 0, length: 50, total: 0 });

    // ── Permission State ───────────────────────────────────────────────────────
    const [permissions, setPermissions] = useState(null);

    // Date picker state
    const [pickerOpen, setPickerOpen] = useState(false);
    const [activePreset, setActivePreset] = useState('Last 7 Days');
    const [customFrom, setCustomFrom] = useState(toInputValue(formatDate(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000))));
    const [customTo, setCustomTo] = useState(toInputValue(formatDate(new Date())));
    const pickerRef = useRef(null);

    // ── Fetch Permissions on Mount ─────────────────────────────────────────────
    // Mirrors the jQuery pattern:
    //   objUserAuth.GetModuleActionList($('[id$=hdnModuleCode]').val(), $('[id$=hdnSipUserId]').val(), objPermId)
    // ModuleCode = PAG10384 (from hidden input), UserId from session
    useEffect(() => {
        const fetchPermissions = async () => {
            try {
                const perm = await getAllPermissionByModuleCodeUserId({
                    Mode: 'S',
                    ModuleCode: MODULE_CODE,
                    UserId: userId,
                    Token: TokenId
                });
                setPermissions(perm);
            } catch (err) {
                console.error('CreditNote permission fetch error:', err);
            }
        };
        if (userId) fetchPermissions();
    }, [userId, TokenId]);

    // ── Click-outside handler for date picker ──────────────────────────────────
    useEffect(() => {
        const handler = (e) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target)) {
                setPickerOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const applyPreset = (preset) => {
        if (preset.getDates) {
            const [s, e] = preset.getDates();
            const from = formatDate(s);
            const to = formatDate(e);
            setDateRange({ from, to });
            setCustomFrom(toInputValue(from));
            setCustomTo(toInputValue(to));
            setActivePreset(preset.label);
            setPickerOpen(false);
        } else {
            setActivePreset('Custom Range');
        }
    };

    const isDateInvalid = useMemo(() => {
        if (!customFrom || !customTo) return false;
        return customTo < customFrom;
    }, [customFrom, customTo]);

    const applyCustomRange = () => {
        if (!customFrom || !customTo) return;
        if (isDateInvalid) {
            alert('To date should not be less than From date');
            return;
        }
        setDateRange({ from: fromInputValue(customFrom), to: fromInputValue(customTo) });
        setActivePreset('Custom Range');
        setPickerOpen(false);
    };

    const fetchList = async () => {
        setIsLoading(true);
        try {
            const params = {
                UserId: userId,
                Token: TokenId,
                FilterText: searchQuery,
                FromDate: dateRange.from,
                ToDate: dateRange.to,
                Status: statusFilter,
                start: pagination.start,
                length: pagination.length,
                draw: 1
            };
            const response = await getCreditNoteList(params);
            if (response && response.data) {
                setCreditNotes(response.data);
                setPagination(prev => ({ ...prev, total: response.recordsTotal || 0 }));
            } else {
                setCreditNotes([]);
                setPagination(prev => ({ ...prev, total: 0 }));
            }
        } catch (err) {
            console.error('Fetch Credit Note List Error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        setPagination(prev => ({ ...prev, start: 0 }));
    }, [searchQuery, statusFilter, dateRange]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => { fetchList(); }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchQuery, statusFilter, dateRange, pagination.start]);

    const stats = useMemo(() => ({
        total: pagination.total,
        open: creditNotes.filter(cn => cn.Status?.toLowerCase() === 'open').length,
        sentForApproval: creditNotes.filter(cn => cn.Status?.toLowerCase()?.includes('approval')).length,
        cancelled: creditNotes.filter(cn => cn.Status?.toLowerCase() === 'cancelled').length
    }), [creditNotes, pagination.total]);

    // ── Permission helpers — map every API flag from Details object ─────────────
    // While permissions are still loading (null), default to true so UI isn't
    // broken. Once the API responds, each flag drives the matching control.
    const canAdd = permissions ? permissions.Add !== false : true;
    const canEdit = permissions ? permissions.Edit !== false : true;
    const canView = permissions ? permissions.View !== false : true;
    const canDelete = permissions ? permissions.Delete !== false : true;
    const canSearch = permissions ? permissions.Search !== false : true;  // ← hides search bar + filter
    const canExport = permissions ? permissions.Export !== false : true;
    const canImport = permissions ? permissions.Import !== false : true;
    const canPrint = permissions ? permissions.Print !== false : true;
    const canDownload = permissions ? permissions.Download !== false : true;
    const canMassUpdate = permissions ? permissions.MassUpdate !== false : true;
    const canMassDelete = permissions ? permissions.MassDelete !== false : true;
    const canSpecial1 = permissions ? permissions.Special1 !== false : true;  // e.g. SendSms
    const canSpecial2 = permissions ? permissions.Special2 !== false : true;
    const canSpecial3 = permissions ? permissions.Special3 !== false : true;

    if (permissions && !canView) {
        return <PermissionDeniedComponent />;
    }
    return (
        <div className="credit-note-module premium-theme">
            {/* Hidden module code — PAG10384 (mirrors hdnModuleCode hidden field) */}
            <input type="hidden" id="hdnModuleCode" name="hdnModuleCode" value={MODULE_CODE} readOnly />

            <SubHeader
                title="Credit Notes"
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                pageName="Credit Note"
                pageCode="PAG10384"
                onSearchClick={canSearch ? fetchList : undefined}
                onAddClick={onAddClick}
                showAdd={canAdd}
                showSearch={canSearch}
                showFilter={false}
                showAdvanceFilter={canSearch}
                showExport={canExport}
                showImport={canImport}
                showViewSwitcher={true}
                showCalendar={false}
                viewMode={viewMode}
                setViewMode={setViewMode}
                showSettings={true}
                onSettingsClick={onSettingsClick}
            >
                <div className="credit-note-filters">
                    {/* Date Range Picker */}
                    <div className="filter-item date-range" style={{ position: 'relative', cursor: 'pointer' }} ref={pickerRef}>
                        <span
                            style={{ fontSize: 13, color: '#374151', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}
                            onClick={() => setPickerOpen(o => !o)}
                        >
                            <Calendar size={15} style={{ color: '#6b7280' }} />
                            {dateRange.from} – {dateRange.to}
                            <ChevronDown size={14} style={{ color: '#9ca3af' }} />
                        </span>

                        {pickerOpen && (
                            <div style={{
                                position: 'absolute', top: '110%', right: 0, zIndex: 9999,
                                background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
                                boxShadow: '0 8px 32px rgba(0,0,0,0.13)', minWidth: 280, padding: '8px 0'
                            }}>
                                {PRESETS.map(preset => (
                                    <div
                                        key={preset.label}
                                        onClick={() => applyPreset(preset)}
                                        style={{
                                            padding: '9px 18px', fontSize: 13, cursor: 'pointer',
                                            color: activePreset === preset.label ? '#2563eb' : '#374151',
                                            fontWeight: activePreset === preset.label ? 700 : 400,
                                            background: activePreset === preset.label ? '#eff6ff' : 'transparent',
                                            borderLeft: activePreset === preset.label ? '3px solid #2563eb' : '3px solid transparent',
                                            transition: 'background 0.15s'
                                        }}
                                        onMouseEnter={e => { if (activePreset !== preset.label) e.currentTarget.style.background = '#f9fafb'; }}
                                        onMouseLeave={e => { if (activePreset !== preset.label) e.currentTarget.style.background = 'transparent'; }}
                                    >
                                        {preset.label}
                                    </div>
                                ))}

                                {activePreset === 'Custom Range' && (
                                    <div style={{ padding: '10px 18px', borderTop: '1px solid #f3f4f6' }}>
                                        <div style={{ marginBottom: 8 }}>
                                            <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, display: 'block', marginBottom: 3 }}>FROM</label>
                                            <input
                                                type="date"
                                                value={customFrom}
                                                onChange={e => setCustomFrom(e.target.value)}
                                                style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
                                            />
                                        </div>
                                        <div style={{ marginBottom: 10 }}>
                                            <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, display: 'block', marginBottom: 3 }}>TO</label>
                                            <input
                                                type="date"
                                                value={customTo}
                                                min={customFrom}
                                                onChange={e => setCustomTo(e.target.value)}
                                                style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
                                            />
                                            {isDateInvalid && (
                                                <div style={{ color: '#ef4444', fontSize: 11, marginTop: 4, fontWeight: 500 }}>
                                                    To date should not be less than From date
                                                </div>
                                            )}
                                        </div>
                                        <button
                                            onClick={applyCustomRange}
                                            disabled={isDateInvalid}
                                            style={{
                                                width: '100%', padding: '7px',
                                                background: isDateInvalid ? '#9ca3af' : '#2563eb',
                                                color: '#fff',
                                                border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600,
                                                cursor: isDateInvalid ? 'not-allowed' : 'pointer'
                                            }}
                                        >
                                            Apply
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="filter-item status-select">
                        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                            {[{ text: 'All', value: '' },
                            { text: 'Open', value: 'Open' },
                            { text: 'Cancelled', value: 'Cancelled' },
                            { text: 'Refunded', value: 'Refunded' },
                            { text: 'Approved', value: 'approved' },
                            { text: 'Rejected', value: 'rejected' },
                            { text: 'Partially Approved', value: 'partially_approved' }
                            ].map((type, idx) => (
                                <option key={idx} value={type.value}>{type.text}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </SubHeader>

            <div className="module-content-wrapper" style={{ padding: '12px 16px', width: '100%', maxWidth: 'none', flex: 1, display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
                <CreditNoteList
                    viewMode={viewMode}
                    data={creditNotes}
                    stats={stats}
                    loading={isLoading}
                    permissions={permissions}
                    onAddClick={onAddClick}
                    onShowClick={onShowClick}
                    onEditClick={onEditClick}
                    onCancelClick={onCancelClick}
                    onDeleteClick={onDeleteClick}
                    onRequestApprovalClick={onRequestApprovalClick}
                    totalRecords={pagination.total}
                    currentPage={Math.floor(pagination.start / pagination.length) + 1}
                    itemsPerPage={pagination.length}
                    onPageChange={(page) => setPagination(prev => ({ ...prev, start: (page - 1) * prev.length }))}
                    onStatusClick={(status) => setStatusFilter(status === 'All' ? '' : status)}
                    searchTerm={searchQuery}
                />
            </div>
        </div>
    );
};

export default CreditNoteModule;
